import { GiftRedemptionsTable } from "@/components/GiftRedemptionsTable";
import EnhancerAgreements from "@/components/enhancer/EnhancerAgreements";
import RichContent from "@/components/RichContent";
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut, Plus, Pencil, Trash2, MapPin, DollarSign, Calendar, Clock, Download, ExternalLink, Save, Loader2, User, Sparkles, ArrowLeft, Info, Link2, TrendingUp, CalendarDays, ShoppingBag, Ticket, BookOpen, Leaf, Users, Building2, Bell, X } from "lucide-react";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ServiceFormModal from "@/components/enhancer/ServiceFormModal";
import EventFormModal from "@/components/enhancer/EventFormModal";
import LocationFormModal from "@/components/enhancer/LocationFormModal";
import ProductFormModal from "@/components/enhancer/ProductFormModal";
import PackageFormModal from "@/components/enhancer/PackageFormModal";
import CourseBuilderModal from "@/components/enhancer/CourseBuilderModal";
import ScheduleExtrasEditor from "@/components/enhancer/ScheduleExtrasEditor";
import { LocationHoursEditor } from "@/components/enhancer/LocationHoursEditor";
import UnifiedCalendar from "@/components/UnifiedCalendar";
import EnhancerBlackouts from "@/components/enhancer/EnhancerBlackouts";
import UpcomingAppointments from "@/components/enhancer/UpcomingAppointments";
import BookingPolicyEditor from "@/components/BookingPolicyEditor";
import ImageUpload from "@/components/ImageUpload";
import AppointmentActions, { StatusBadge } from "@/components/AppointmentActions";
import CenterApprovalsPanel from "@/components/enhancer/CenterApprovalsPanel";
import { CentersSchedulePanel } from "@/components/enhancer/CentersSchedulePanel";
import MediaFormModal from "@/components/enhancer/MediaFormModal";
import YouTubeSyncCard from "@/components/enhancer/YouTubeSyncCard";
import ConnectPayoutCard from "@/components/enhancer/ConnectPayoutCard";
import PartnerLedgerCard from "@/components/enhancer/PartnerLedgerCard";
import MyRentalsCard from "@/components/enhancer/MyRentalsCard";
import ConnectNudge from "@/components/enhancer/ConnectNudge";
import CreatorAnalytics from "@/components/enhancer/CreatorAnalytics";
import StewardshipFormModal from "@/components/enhancer/StewardshipFormModal";
import GoogleCalendarConnect from "@/components/GoogleCalendarConnect";
import DashboardHero from "@/components/DashboardHero";
import RoleHome from "@/components/dashboard/RoleHome";
import SetupChecklist from "@/components/dashboard/SetupChecklist";
import MyClients from "@/components/enhancer/MyClients";
import SessionPrep from "@/components/enhancer/SessionPrep";
import { ENHANCER_FEATURES } from "@/lib/featureDescriptions";
import EventRoster from "@/components/enhancer/EventRoster";
import MyShifts from "@/components/enhancer/MyShifts";
import SellerOrders from "@/components/SellerOrders";
import IntegrationsPanel from "@/components/enhancer/IntegrationsPanel";
import NotificationsPanel from "@/components/NotificationsPanel";
import IntakeFormEditor from "@/components/enhancer/IntakeFormEditor";
import FormBuilderPanel from "@/components/FormBuilderPanel";
import SocialPanel from "@/components/enhancer/SocialPanel";
import EnhancerArticlesPanel from "@/components/enhancer/EnhancerArticlesPanel";
import InquiriesPanel from "@/components/InquiriesPanel";
import GalleryEditor from "@/components/enhancer/GalleryEditor";
import { Inbox, RadioTower } from "lucide-react";
import { BroadcastingHub } from "@/components/media/BroadcastingHub";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";
import PosMovedCard from "@/components/PosMovedCard";
import { HostedPrograms } from "@/components/programs/ProgramAdmin";
import { ClaimPanel } from "@/components/ClaimPanel";

import RichTextEditor from "@/components/RichTextEditor";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Small inline component for adding a certification tag
function CertInput({ onAdd }) {
  const [val, setVal] = useState("");
  const submit = () => {
    const trimmed = val.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setVal("");
  };
  return (
    <div className="flex gap-2">
      <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), submit())}
        placeholder="e.g. Reiki Level 2 – USUI Method" data-testid="enh-cert-input" className="eyw-input flex-1 text-sm" />
      <button type="button" onClick={submit} disabled={!val.trim()} data-testid="enh-cert-add" className="eyw-btn-ghost !py-2 !px-4 text-sm disabled:opacity-40"><Plus size={14} /> Add</button>
    </div>
  );
}
const NAV_GROUPS = [
  { key: "overview", label: "Overview", icon: Sparkles, items: [["Get Started", Sparkles], ["Analytics", TrendingUp]] },
  { key: "presence", label: "My Presence", icon: User, items: [["Profile", User], ["Articles", BookOpen], ["Social", Link2]] },
  { key: "media", label: "Media & Broadcasting", icon: RadioTower, items: [["Media & Broadcasting", RadioTower]] },
  { key: "schedule", label: "Schedule & Front Desk", icon: Calendar, items: [["Availability", Clock], ["Bookings", Calendar], ["Clients", Users], ["Intake Form", BookOpen], ["Forms", BookOpen], ["Agreements", BookOpen], ["Event Roster", Users], ["My Shifts", CalendarDays]] },
  { key: "offerings", label: "Offerings & Facilities", icon: Sparkles, items: [["Services", Sparkles], ["Events", CalendarDays], ["Products", ShoppingBag], ["Packages", Ticket], ["Courses", BookOpen], ["Stewardship", Leaf]] },
  { key: "places", label: "Centers & Schedule", icon: Building2, items: [["Centers & Schedule", Building2]] },
  { key: "finance", label: "Finance & Sales", icon: DollarSign, items: [["Earnings", DollarSign], ["Orders", ShoppingBag]] },
  { key: "communication", label: "Communication & Feedback", icon: Bell, items: [["Notifications", Bell], ["Inbox", Bell]] },
  { key: "integrations", label: "Integrations", icon: Link2, items: [["Integrations", Link2]] },
];
const ALL_TABS = NAV_GROUPS.flatMap((g) => g.items.map(([k]) => k));
// Old tab names (links, bookmarks) open the merged section at the matching view.
const CS_LEGACY = { "Locations": "spaces", "Our Centers": "centers", "Schedule at EYW": "rooms" };
const resolveTab = (raw) => {
  if (["Media", "media", "broadcast"].includes(raw)) return "Media & Broadcasting";
  return CS_LEGACY[raw] ? "Centers & Schedule" : raw;
};
const badge = (s) => s === "approved" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : s === "rejected" ? "bg-red-100 text-red-600" : "bg-[#D98E73]/15 text-[#B5794E]";

export default function EnhancerDashboard() {
  const { user, logout, setSession } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const t0 = resolveTab(q.get("tab"));
    if (ALL_TABS.includes(t0)) return t0;
    if (q.get("youtube_live") || q.get("yt_live")) return "Media & Broadcasting";
    return (q.get("zoom") || q.get("gcal")) ? "Integrations" : "Get Started";
  });
  const [csView, setCsView] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return CS_LEGACY[q.get("tab")] || q.get("view") || "calendar";
  });
  const routerLoc = useLocation();
  useEffect(() => {
    const raw = new URLSearchParams(routerLoc.search).get("tab");
    const t = resolveTab(raw);
    if (t && ALL_TABS.includes(t)) setTab(t);
    if (CS_LEGACY[raw]) setCsView(CS_LEGACY[raw]);
    else if (raw === "Centers & Schedule" && new URLSearchParams(routerLoc.search).get("view")) setCsView(new URLSearchParams(routerLoc.search).get("view"));
  }, [routerLoc.search]);
  const [svcModal, setSvcModal] = useState(null); // null closed | {} new | service edit
  const [evtModal, setEvtModal] = useState(null);
  const [locModal, setLocModal] = useState(null);
  const [prodModal, setProdModal] = useState(null);
  const [pkgModal, setPkgModal] = useState(null);
  const [courseModal, setCourseModal] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [stmtMonth, setStmtMonth] = useState(new Date().toISOString().slice(0, 7));
  const [dateTo, setDateTo] = useState("");
  const [prof, setProf] = useState({
    name: user?.name || "", headline: user?.headline || "", bio: user?.bio || "",
    avatar: user?.avatar || "", location: user?.location || "",
    specialties: (user?.specialties || []).join(", "),
    website: user?.website || "",
    social_links: user?.social_links || {},
    timezone: user?.timezone || "",
    show_in_directory: user?.show_in_directory !== false,
    cover_banner: user?.cover_banner || "",
    years_experience: user?.years_experience || "",
    certifications: user?.certifications || [],
    gallery: user?.gallery || [],
  });
  const [savingProf, setSavingProf] = useState(false);
  const [mediaModal, setMediaModal] = useState(null);
  const [stewardModal, setStewardModal] = useState(null);
  const setSocial = (k) => (e) => setProf((p) => ({ ...p, social_links: { ...p.social_links, [k]: e.target.value } }));

  // Re-sync the profile form once the logged-in user loads (session arrives async via /auth/me).
  // Keyed on user id so it never clobbers edits the user is actively making.
  useEffect(() => {
    if (!user) return;
    setProf({
      name: user.name || "", headline: user.headline || "", bio: user.bio || "",
      avatar: user.avatar || "", location: user.location || "",
      specialties: (user.specialties || []).join(", "),
      website: user.website || "",
      social_links: user.social_links || {},
      timezone: user.timezone || "",
      show_in_directory: user.show_in_directory !== false,
      cover_banner: user.cover_banner || "",
      years_experience: user.years_experience || "",
      certifications: user.certifications || [],
      gallery: user.gallery || [],
    });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const profDirty = !!user && (
    prof.name !== (user.name || "") || prof.headline !== (user.headline || "") ||
    prof.bio !== (user.bio || "") || prof.avatar !== (user.avatar || "") ||
    prof.location !== (user.location || "") ||
    prof.specialties !== (user.specialties || []).join(", ") ||
    prof.website !== (user.website || "") || prof.timezone !== (user.timezone || "") ||
    (prof.show_in_directory !== (user.show_in_directory !== false))
  );

  const services = useQuery({ queryKey: ["enh-services"], queryFn: () => api.get("/enhancer/me/services").then(r => r.data) });
  const myEvents = useQuery({ queryKey: ["enh-events"], queryFn: () => api.get("/enhancer/me/events").then(r => r.data) });
  const products = useQuery({ queryKey: ["enh-products"], queryFn: () => api.get("/enhancer/me/products").then(r => r.data) });
  const packages = useQuery({ queryKey: ["enh-packages"], queryFn: () => api.get("/enhancer/me/packages").then(r => r.data) });  const courses = useQuery({ queryKey: ["enh-courses"], queryFn: () => api.get("/enhancer/me/courses").then(r => r.data) });
  const locations = useQuery({ queryKey: ["enh-locations"], queryFn: () => api.get("/enhancer/me/locations").then(r => r.data) });
  const schedLocations = useQuery({ queryKey: ["enh-sched-locations"], queryFn: () => api.get("/enhancer/me/schedulable-locations").then(r => r.data.locations) });
  const availability = useQuery({ queryKey: ["enh-availability"], queryFn: () => api.get("/enhancer/me/availability").then(r => r.data) });
  const bookings = useQuery({ queryKey: ["enh-bookings"], queryFn: () => api.get("/enhancer/me/bookings").then(r => r.data) });
  const connect = useQuery({ queryKey: ["enh-connect"], queryFn: () => api.get("/enhancer/connect/status").then(r => r.data), retry: false });
  const home = useQuery({ queryKey: ["enh-home"], queryFn: () => api.get("/enhancer/me/home").then(r => r.data) });
  const media = useQuery({ queryKey: ["enh-media"], queryFn: () => api.get("/media/mine").then(r => r.data) });
  const steward = useQuery({ queryKey: ["enh-steward"], queryFn: () => api.get("/enhancer/stewardship/opportunities").then(r => r.data) });
  const earnings = useQuery({
    queryKey: ["enh-earnings", dateFrom, dateTo],
    queryFn: () => api.get(`/enhancer/me/earnings?date_from=${dateFrom}&date_to=${dateTo}`).then(r => r.data),
  });

  const refetch = (k) => qc.invalidateQueries({ queryKey: [k] });

  const saveProfile = async () => {
    setSavingProf(true);
    try {
      await api.put("/me/profile", {
        name: prof.name, headline: prof.headline, bio: prof.bio, avatar: prof.avatar,
        location: prof.location, show_in_directory: prof.show_in_directory,
        website: prof.website, social_links: prof.social_links, timezone: prof.timezone,
        specialties: prof.specialties.split(",").map((s) => s.trim()).filter(Boolean),
        cover_banner: prof.cover_banner || "",
        years_experience: prof.years_experience ? parseInt(prof.years_experience, 10) : null,
        certifications: (prof.certifications || []).filter(Boolean),
        gallery: (prof.gallery || []).filter(Boolean),
      });
      toast.success("Profile saved");
      try { const { data: me } = await api.get("/auth/me"); setSession({ user: me }); } catch { /* best-effort refresh */ }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingProf(false); }
  };

  const exportEarnings = async () => {
    try {
      const res = await api.get(`/enhancer/me/earnings.csv?date_from=${dateFrom}&date_to=${dateTo}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "my-earnings.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export earnings"); }
  };

  const downloadStatement = async () => {
    if (!stmtMonth) { toast.error("Pick a month first."); return; }
    try {
      const res = await api.get(`/enhancer/me/commission-statement?month=${stmtMonth}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `eyw-commission-${stmtMonth}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Commission statement downloaded.");
    } catch { toast.error("Could not generate your statement."); }
  };

  const exportPayouts = async () => {
    try {
      const res = await api.get(`/enhancer/me/payouts.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "my-payout-history.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export payout history"); }
  };

  const delService = async (id) => { if (!window.confirm("Delete this service? This cannot be undone.")) return; await api.delete(`/enhancer/services/${id}`); toast.success("Service deleted"); refetch("enh-services"); };
  const delEvent = async (id) => { if (!window.confirm("Delete this event? This cannot be undone.")) return; await api.delete(`/enhancer/events/${id}?scope=all`); toast.success("Event deleted"); refetch("enh-events"); };
  const delLocation = async (id) => { if (!window.confirm("Delete this location? This cannot be undone.")) return; await api.delete(`/enhancer/locations/${id}`); toast.success("Location deleted"); refetch("enh-locations"); };
  const delProduct = async (id) => { if (!window.confirm("Delete this product? This cannot be undone.")) return; await api.delete(`/enhancer/products/${id}`); toast.success("Product deleted"); refetch("enh-products"); };
  const delPackage = async (id) => { if (!window.confirm("Delete this package? This cannot be undone.")) return; await api.delete(`/enhancer/packages/${id}`); toast.success("Package deleted"); refetch("enh-packages"); };
  const delCourse = async (id) => { if (!window.confirm("Delete this course? Enrolled members will lose access. This cannot be undone.")) return; await api.delete(`/enhancer/courses/${id}`); toast.success("Course deleted"); refetch("enh-courses"); };

  const [slots, setSlots] = useState(null);
  const editSlots = slots ?? (availability.data?.slots || []);
  const saveSlots = async () => {
    try {
      await api.put("/enhancer/availability", { slots: editSlots });
      toast.success("Availability saved"); setSlots(null); refetch("enh-availability");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save availability");
    }
  };

  const navGroups = NAV_GROUPS.map((g) => ({
    key: g.key, label: g.label, icon: g.icon,
    items: g.items.map(([key, icon]) => ({ key, label: key === "Get Started" ? "Home" : key, icon, badge: 0, desc: ENHANCER_FEATURES[key] })),
  }));
  const quickActions = [];
  if (user?.id) quickActions.push({ label: "My storefront", icon: ExternalLink, onClick: () => window.open(`/enhancers/${user.id}`, "_blank"), testid: "enh-view-storefront-top" });
  if (user?.manages_location_id) quickActions.push({ label: "My Center", icon: MapPin, onClick: () => nav("/manager"), testid: "enh-manage-center-link" });
  if (user?.role === "admin" || user?.role === "staff") quickActions.push({ label: "Admin", icon: ArrowLeft, onClick: () => nav("/admin"), testid: "enh-back-to-admin-link" });

  return (
    <DashboardShell
      role="enhancer"
      brandTitle="Enhancer Studio"
      scopeName={user?.name ? `${user.name} · Personal studio` : "Personal studio"}
      nav={navGroups}
      activeKey={tab}
      onSelect={setTab}
      quickActions={quickActions}
      user={user}
      onLogout={logout}
    >
      <div data-testid="enhancer-dashboard">
      {tab === "Get Started" ? (
        <RoleHome endpoint="/enhancer/me/home" queryKey={["enh-home"]} title={`Welcome back${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`}
          go={(k) => (k === "unread" ? nav("/messages") : setTab(k === "unpaid" ? "Earnings" : "Bookings"))}
          quickLinks={[
            { label: "Front desk / POS", onClick: () => nav("/pos") },
            { label: "My bookings", onClick: () => setTab("Bookings") },
            { label: "Availability", onClick: () => setTab("Availability") },
            { label: "Media & Broadcasting", onClick: () => setTab("Media & Broadcasting") },
            { label: "Earnings", onClick: () => setTab("Earnings") },
            { label: "Messages", onClick: () => nav("/messages") },
          ]}>
          {home.data && (
            <SetupChecklist role="enhancer" dismissed={home.data.checklist_dismissed} go={setTab} steps={[
              { key: "profile", done: !!(user?.avatar && (user?.bio || user?.headline)), tab: "Profile" },
              { key: "offering", done: (services.data?.length || 0) > 0, tab: "Services" },
              { key: "payouts", done: !!connect.data?.payouts_enabled, tab: "Earnings" },
              { key: "availability", done: (availability.data?.slots?.length || 0) > 0, tab: "Availability" },
              { key: "media", done: (home.data.media_count || 0) > 0, tab: "Media & Broadcasting" },
            ]} />
          )}
        </RoleHome>
      ) : tab === "Bookings" ? <UpcomingAppointments /> : null}
      <ConnectNudge onGoToEarnings={() => setTab("Earnings")} />

      {tab === "Profile" && (
        <div data-testid="enh-profile-panel" className="max-w-2xl">
          <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
            <h2 className="font-serif text-2xl text-[#1A242B]">Public profile</h2>
            <Link to={`/enhancers/${user?.id}`} target="_blank" data-testid="enh-view-public" className="eyw-btn-ghost !py-2 text-sm"><ExternalLink size={15} /> View public page</Link>
            <button type="button" data-testid="enh-copy-referral-link" className="eyw-btn-ghost !py-2 text-sm"
              onClick={() => { const url = `${window.location.origin}/enhancers/${user?.id}?ref=${user?.id}`; navigator.clipboard?.writeText(url).then(() => toast.success("Referral link copied — clients who book through it are credited to you."), () => window.prompt("Copy your referral link:", url)); }}>
              <Link2 size={15} /> Copy my referral link
            </button>
          </div>
          <div data-testid="enh-profile-howto" className="eyw-card p-5 mb-5 border-l-4 border-[#144F75] bg-[#144F75]/[0.04]">
            <div className="flex items-start gap-3">
              <Info size={18} className="text-[#144F75] mt-0.5 shrink-0" />
              <div className="text-sm text-[#33454F] space-y-2">
                <p className="font-semibold text-[#1A242B]">How to set up your Enhancer profile</p>
                <ol className="list-decimal ml-4 space-y-1.5">
                  <li><span className="font-medium">Add your details</span> — display name, a short headline (e.g. "Reiki Master &amp; Sound Healer"), your specialties and location.</li>
                  <li><span className="font-medium">Upload a photo</span> — click <span className="font-medium">Upload</span> under "Profile photo" and choose a clear headshot. Wait for the "Image uploaded" confirmation.</li>
                  <li><span className="font-medium">Write a bio</span> and add any website or social links you'd like members to see.</li>
                  <li><span className="font-medium">Add certifications</span> — each credential displays as a verified badge on your profile, building trust with potential clients.</li>
                  <li><span className="font-medium">Upload gallery photos</span> — show your space, products, or in-session moments to help people feel at ease before booking.</li>
                  <li><span className="font-medium">Click "Save profile"</span> at the bottom — <span className="font-medium text-[#B5794E]">changes are not live until you save.</span></li>
                </ol>
              </div>
            </div>
          </div>

          {/* Card 1: Core identity */}
          <div className="eyw-card p-6 space-y-4 mb-4">
            <h3 className="font-medium text-[#1A242B] text-sm uppercase tracking-wide text-[#8FA89B]">Core identity</h3>
            <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Display name</label><input value={prof.name} onChange={(e) => setProf({ ...prof, name: e.target.value })} data-testid="enh-prof-name" className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Headline <span className="font-normal text-[#8FA89B]">(shown under your name)</span></label><input value={prof.headline} onChange={(e) => setProf({ ...prof, headline: e.target.value })} placeholder="e.g. Reiki Master & Sound Healer" data-testid="enh-prof-headline" className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Specialties <span className="font-normal text-[#8FA89B]">(comma separated)</span></label><input value={prof.specialties} onChange={(e) => setProf({ ...prof, specialties: e.target.value })} placeholder="Reiki, Breathwork, Meditation" data-testid="enh-prof-specialties" className="eyw-input w-full" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Location</label><input value={prof.location} onChange={(e) => setProf({ ...prof, location: e.target.value })} placeholder="City, State" data-testid="enh-prof-location" className="eyw-input w-full" /></div>
              <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Years of experience</label><input type="number" min="0" max="60" value={prof.years_experience} onChange={(e) => setProf({ ...prof, years_experience: e.target.value })} placeholder="e.g. 8" data-testid="enh-prof-years" className="eyw-input w-full" /></div>
            </div>
            <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Bio</label><RichTextEditor value={prof.bio} onChange={(v) => setProf((p) => ({ ...p, bio: v }))} testid="enh-prof-bio" placeholder="Tell clients about you, your approach and training…" minHeight={150} enableBlocks={false} /></div>
          </div>

          {/* Card 2: Photos & visuals */}
          <div className="eyw-card p-6 space-y-5 mb-4">
            <h3 className="font-medium text-sm uppercase tracking-wide text-[#8FA89B]">Photos &amp; visuals</h3>
            <div>
              <ImageUpload value={prof.avatar} onChange={(url) => setProf({ ...prof, avatar: url })} label="Profile photo (headshot)" rounded testid="enh-prof-avatar" />
              <p className="text-xs text-[#8A7B68] mt-1.5">After uploading, click <span className="font-medium">Save profile</span> below to publish it.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A242B] mb-1 block">Cover banner <span className="font-normal text-[#8FA89B]">(optional wide header image)</span></label>
              <ImageUpload value={prof.cover_banner} onChange={(url) => setProf({ ...prof, cover_banner: url })} label="" testid="enh-prof-banner" />
              <p className="text-xs text-[#8A7B68] mt-1">Ideal size: 1400×350px. Shows as a wide banner behind your profile header.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A242B] mb-2 block">Photo gallery <span className="font-normal text-[#8FA89B]">(up to 8 photos)</span></label>
              <GalleryEditor value={prof.gallery} onChange={(urls) => setProf({ ...prof, gallery: urls })} />
              <p className="text-xs text-[#8A7B68] mt-1.5">Show your space, products, events or in-session moments. Visitors see these in a grid on your profile.</p>
            </div>
          </div>

          {/* Card 3: Certifications */}
          <div className="eyw-card p-6 space-y-4 mb-4">
            <div>
              <h3 className="font-medium text-sm uppercase tracking-wide text-[#8FA89B] mb-1">Certifications &amp; credentials</h3>
              <p className="text-xs text-[#8FA89B]">Each entry displays as a verified badge on your public profile. Add them one at a time.</p>
            </div>
            {/* Existing tags */}
            {(prof.certifications || []).length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="enh-cert-list">
                {prof.certifications.map((c, i) => (
                  <span key={i} data-testid={`enh-cert-tag-${i}`} className="inline-flex items-center gap-1.5 bg-[#5C8A72]/10 text-[#3A6B57] text-xs rounded-full px-3 py-1.5 font-medium border border-[#5C8A72]/20">
                    {c}
                    <button type="button" onClick={() => setProf({ ...prof, certifications: prof.certifications.filter((_, j) => j !== i) })}
                      data-testid={`enh-cert-remove-${i}`} className="hover:text-[#B45151] ml-0.5"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
            {/* Add new */}
            <CertInput onAdd={(val) => setProf((p) => ({ ...p, certifications: [...(p.certifications || []), val] }))} />
          </div>

          {/* Card 4: Contact & links */}
          <div className="eyw-card p-6 space-y-4 mb-4">
            <h3 className="font-medium text-sm uppercase tracking-wide text-[#8FA89B]">Contact &amp; links</h3>
            <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Website</label><input value={prof.website} onChange={(e) => setProf({ ...prof, website: e.target.value })} placeholder="https://…" data-testid="enh-prof-website" className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Timezone</label><input value={prof.timezone} onChange={(e) => setProf({ ...prof, timezone: e.target.value })} placeholder="e.g. America/New_York" data-testid="enh-prof-timezone" className="eyw-input w-full" /></div>
            <div>
              <label className="text-sm font-medium text-[#1A242B] mb-1 block">Social &amp; media links</label>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="enh-prof-social">
                {["instagram", "facebook", "youtube", "spotify", "tiktok", "podcast"].map((k) => (
                  <input key={k} value={prof.social_links?.[k] || ""} onChange={setSocial(k)} data-testid={`enh-social-${k}`}
                    placeholder={`${k[0].toUpperCase() + k.slice(1)} URL`} className="eyw-input w-full" />
                ))}
              </div>
            </div>
            <label className="flex items-center justify-between">
              <span className="text-sm font-medium text-[#1A242B]">List me in the public Enhancer directory</span>
              <button type="button" onClick={() => setProf({ ...prof, show_in_directory: !prof.show_in_directory })} data-testid="enh-prof-visibility"
                className={`relative h-7 w-12 rounded-full transition-colors ${prof.show_in_directory ? "bg-[#144F75]" : "bg-[#E5E0D8]"}`}>
                <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${prof.show_in_directory ? "left-6" : "left-1"}`} />
              </button>
            </label>
          </div>

          <div className="flex justify-end items-center gap-3 pb-4">
            {profDirty && <span data-testid="enh-prof-unsaved" className="text-xs font-medium text-[#B5794E]">You have unsaved changes</span>}
            <button onClick={saveProfile} disabled={savingProf} data-testid="enh-prof-save" className={`eyw-btn !py-2.5 text-sm disabled:opacity-60 ${profDirty ? "ring-2 ring-[#D98E73] ring-offset-2" : ""}`}>{savingProf && <Loader2 className="animate-spin" size={16} />} <Save size={15} /> Save profile</button>
          </div>
        </div>
      )}

      {tab === "Media & Broadcasting" && (
        <div data-testid="enh-media-panel">
          <BroadcastingHub />
          <YouTubeSyncCard onSynced={() => { refetch("enh-media"); qc.invalidateQueries({ queryKey: ["media-hub-manage"] }); }} />
        </div>
      )}

      {tab === "Social" && <SocialPanel />}
      {tab === "Articles" && <EnhancerArticlesPanel />}

      {tab === "Notifications" && <NotificationsPanel />}
      {tab === "Inbox" && <InquiriesPanel basePath="/enhancer" title="Inbox" subtitle="Messages from visitors and prospective members who found your profile — reply here and they'll get it by email." />}

      {tab === "Stewardship" && (
        <div data-testid="enh-steward-panel">
          <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
            <div>
              <h2 className="font-serif text-2xl text-[#1A242B]">Stewardship events & programs</h2>
              <p className="text-sm text-[#4A5D6B]">Propose community events and programs. Each submission is reviewed by the EYW team before it goes live.</p>
            </div>
            <button onClick={() => setStewardModal({})} data-testid="enh-steward-add" className="eyw-btn !py-2 text-sm"><Plus size={15} /> Propose</button>
          </div>
          <div className="grid gap-3">
            {(steward.data || []).map((o) => (
              <div key={o.id} data-testid={`enh-steward-${o.id}`} className="flex items-center justify-between bg-white rounded-xl border border-[#E5E0D8] px-4 py-3">
                <div>
                  <p className="font-medium text-[#1A242B]">{o.title}</p>
                  <p className="text-xs text-[#8FA89B] capitalize">{o.opportunity_type} · {o.category}
                    {o.moderation_status === "pending" && <span className="ml-2 text-[#8a6d1f]">· pending review</span>}
                    {o.moderation_status === "approved" && <span className="ml-2 text-[#5C8A72]">· published</span>}
                    {o.moderation_status === "rejected" && <span className="ml-2 text-[#B45151]">· not approved</span>}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => setStewardModal(o)} className="text-sm text-[#144F75] hover:underline">Edit</button>
                  <button onClick={async () => { if (window.confirm("Delete this submission?")) { await api.delete(`/enhancer/stewardship/opportunities/${o.id}`); refetch("enh-steward"); toast.success("Deleted — your changes are live"); } }} className="text-[#B45151]"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
            {(steward.data || []).length === 0 && <p className="text-sm text-[#4A5D6B]">No submissions yet. Propose your first stewardship event or program.</p>}
          </div>
        </div>
      )}

      {tab === "Services" && (
        <div data-testid="enh-services-panel">
          <CenterApprovalsPanel />
          <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
            <div>
              <h2 className="font-serif text-2xl text-[#1A242B]">My custom offerings</h2>
              <p className="text-xs text-[#4A5D6B]">Your own/virtual/partner-location offerings with custom names. Center services appear above (admin-approved).</p>
            </div>
            <button onClick={() => setSvcModal({})} data-testid="enh-add-service" className="eyw-btn !py-2 text-sm"><Plus size={16} /> Add offering</button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {(services.data || []).map((s) => (
              <div key={s.id} className="eyw-card p-6" data-testid={`enh-service-${s.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg text-[#1A242B]">{s.title}</h3>
                    <p className="text-xs text-[#4A5D6B]">{s.category} · {s.duration_min} min · {money(s.price)}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${s.published ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#D98E73]/15 text-[#B5794E]"}`} data-testid={`enh-service-status-${s.id}`}>{s.published ? "Live" : "Draft"}</span>
                </div>
                {s.center_host_status && (
                  <div className="mt-2" data-testid={`enh-service-host-${s.id}`}>
                    {s.center_host_status === "pending" && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#D98E73]/15 text-[#B5794E]">In-center: pending at {s.center_host_location_name || "center"}</span>}
                    {s.center_host_status === "approved" && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#5C8A72]/15 text-[#5C8A72]">In-center: approved · {s.center_host_location_name || "center"}</span>}
                    {s.center_host_status === "rejected" && <span title={s.center_host_note || ""} className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-100 text-red-600">In-center: not approved · online only</span>}
                  </div>
                )}
                <p className="text-xs text-[#8FA89B] mt-2">Revenue: {s.revenue_model} ({s.enhancer_pct}% you / {s.ecosystem_pct}% eco{s.partner_pct ? ` / ${s.partner_pct}% partner` : ""})</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setSvcModal(s)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Pencil size={13} /> Edit</button>
                  <button onClick={() => delService(s.id)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600 hover:!bg-red-500"><Trash2 size={13} /> Delete</button>
                </div>
              </div>
            ))}
            {services.data?.length === 0 && <p className="text-[#4A5D6B] text-sm">No services yet. Add your first offering.</p>}
          </div>
        </div>
      )}

      {tab === "Events" && (
        <div data-testid="enh-events-panel">
          <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
            <div>
              <h2 className="font-serif text-2xl text-[#1A242B]">My events</h2>
              <p className="text-xs text-[#4A5D6B]">Circles, workshops and gatherings you host — online, at your own location, or at a center you're approved at. Invite co-facilitators and set the revenue split.</p>
            </div>
            <button onClick={() => setEvtModal({})} data-testid="enh-add-event" className="eyw-btn !py-2 text-sm"><Plus size={16} /> New event</button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {(myEvents.data || []).map((e) => (
              <div key={e.id} className="eyw-card p-6" data-testid={`enh-event-${e.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg text-[#1A242B]">{e.title}</h3>
                    <p className="text-xs text-[#4A5D6B]">{e.category} · {fmtDate(e.date)} {fmtTimeRange(e.start_time)} · {e.location || "Online"} · {money((e.price || 0))}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${e.published ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>{e.published ? "Live" : "Draft"}</span>
                </div>
                {(e.collaborators || []).length > 0 && <p className="text-xs text-[#8FA89B] mt-2">Co-facilitators: {(e.collaborators || []).map((c) => `${c.name} (${c.pct}%)`).join(", ")}</p>}
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setEvtModal(e)} data-testid={`enh-event-edit-${e.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Pencil size={13} /> Edit</button>
                  <button onClick={() => delEvent(e.id)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600 hover:!bg-red-500"><Trash2 size={13} /> Delete</button>
                </div>
              </div>
            ))}
            {myEvents.data?.length === 0 && <p className="text-[#4A5D6B] text-sm">No events yet. Create your first circle, workshop, or gathering.</p>}
          </div>
        </div>
      )}

      {tab === "Products" && (
        <div data-testid="enh-products-panel">
          <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
            <h2 className="font-serif text-2xl text-[#1A242B]">My products</h2>
            <button onClick={() => setProdModal({})} data-testid="enh-add-product" className="eyw-btn !py-2 text-sm"><Plus size={16} /> Add product</button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {(products.data || []).map((p) => (
              <div key={p.id} className="eyw-card p-6" data-testid={`enh-product-${p.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg text-[#1A242B]">{p.title}</h3>
                    <p className="text-xs text-[#4A5D6B]">{p.category} · {p.type} · {money(p.price)}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${badge(p.approval_status)}`}>{p.approval_status}</span>
                </div>
                <p className="text-xs text-[#8FA89B] mt-2">Revenue: {p.revenue_model} ({p.enhancer_pct}% you / {p.ecosystem_pct}% eco)</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setProdModal(p)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Pencil size={13} /> Edit</button>
                  <button onClick={() => delProduct(p.id)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600 hover:!bg-red-500"><Trash2 size={13} /> Delete</button>
                </div>
              </div>
            ))}
            {products.data?.length === 0 && <p className="text-[#4A5D6B] text-sm">No products yet. List a physical or digital product for the marketplace.</p>}
          </div>
        </div>
      )}

      {tab === "Packages" && (
        <div data-testid="enh-packages-panel">
          <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
            <div>
              <h2 className="font-serif text-2xl text-[#1A242B]">My session packages</h2>
              <p className="text-xs text-[#4A5D6B]">Bundle your own sessions and sell them from your storefront. Live immediately — sessions redeem for your offerings.</p>
            </div>
            <button onClick={() => setPkgModal({})} data-testid="enh-add-package" className="eyw-btn !py-2 text-sm"><Plus size={16} /> Add package</button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {(packages.data || []).map((p) => (
              <div key={p.id} className="eyw-card p-6" data-testid={`enh-package-${p.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg text-[#1A242B]">{p.name}</h3>
                    <p className="text-xs text-[#4A5D6B]">{p.sessions} sessions · {money(p.price)} · {p.eligible_service_ids?.length || 0} offering(s)</p>
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[#5C8A72]/15 text-[#5C8A72]">Live</span>
                </div>
                {p.description && <RichContent preview text={p.description} className="text-xs text-[#8FA89B] mt-2 line-clamp-3" />}
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setPkgModal(p)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Pencil size={13} /> Edit</button>
                  <button onClick={() => delPackage(p.id)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600 hover:!bg-red-500"><Trash2 size={13} /> Delete</button>
                </div>
              </div>
            ))}
            {packages.data?.length === 0 && <p className="text-[#4A5D6B] text-sm">No packages yet. Bundle a few of your sessions into a package to sell.</p>}
          </div>
          <HostedPrograms />
        </div>
      )}

      {tab === "Courses" && (
        <div data-testid="enh-courses-panel">
          <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
            <div>
              <h2 className="font-serif text-2xl text-[#1A242B]">My courses</h2>
              <p className="text-sm text-[#4A5D6B]">Build self-paced courses with video lessons, downloads, and discussion.</p>
            </div>
            <button onClick={() => setCourseModal({})} data-testid="enh-add-course" className="eyw-btn !py-2 text-sm"><Plus size={16} /> New course</button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {(courses.data || []).map((c) => (
              <div key={c.id} className="eyw-card p-6" data-testid={`enh-course-${c.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <h3 className="text-lg text-[#1A242B]">{c.title}</h3>
                    <p className="text-xs text-[#4A5D6B]">{c.category} · {c.lesson_count} lessons · {c.access === "paid" ? `${money((c.price || 0))}` : c.access === "membership" ? "Members" : "Free"}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${c.published ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>{c.published ? "Published" : "Draft"}</span>
                </div>
                <p className="text-xs text-[#8FA89B] mt-2">{c.enrolled_count} enrolled · Revenue {c.enhancer_pct}% you / {c.ecosystem_pct}% eco</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setCourseModal(c)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Pencil size={13} /> Edit</button>
                  <Link to={`/courses/${c.id}`} target="_blank" className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><ExternalLink size={13} /> View</Link>
                  <button onClick={() => delCourse(c.id)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600 hover:!bg-red-500"><Trash2 size={13} /> Delete</button>
                </div>
              </div>
            ))}
            {courses.data?.length === 0 && <p className="text-[#4A5D6B] text-sm">No courses yet. Create your first course — add sections, video lessons, and downloads.</p>}
          </div>
        </div>
      )}

      {tab === "Availability" && (
        <div data-testid="enh-availability-panel">
          <div className="mb-8">
            <h2 className="font-serif text-2xl text-[#1A242B] mb-3">My calendar</h2>
            <UnifiedCalendar scope="enhancer" />
          </div>
          <div className="mb-6"><GoogleCalendarConnect /></div>
          <LocationHoursEditor slots={editSlots} onChange={setSlots} onSave={saveSlots} locations={schedLocations.data || []} />
          <ScheduleExtrasEditor locations={locations.data || []} />
          <EnhancerBlackouts />
          <div className="mt-6"><BookingPolicyEditor base="enhancer" testid="enh-booking-policy" /></div>
        </div>
      )}

      {tab === "Centers & Schedule" && <CentersSchedulePanel view={csView} setView={setCsView} spacesPanel={(
        <div data-testid="enh-locations-panel">
          <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
            <h2 className="font-serif text-2xl text-[#1A242B]">My locations</h2>
            <button onClick={() => setLocModal({})} data-testid="enh-add-location" className="eyw-btn !py-2 text-sm"><Plus size={16} /> Add location</button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {(locations.data || []).map((l) => (
              <div key={l.id} className="eyw-card p-6" data-testid={`enh-location-${l.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg text-[#1A242B] flex items-center gap-2"><MapPin size={16} className="text-[#D98E73]" /> {l.name}</h3>
                    <p className="text-xs text-[#4A5D6B]">{l.type} · {l.city}{l.state ? `, ${l.state}` : ""}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${badge(l.approval_status)}`}>{l.approval_status}</span>
                </div>
                <p className="text-xs text-[#8FA89B] mt-2">Privacy: {l.privacy}</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setLocModal(l)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Pencil size={13} /> Edit</button>
                  <button onClick={() => delLocation(l.id)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600 hover:!bg-red-500"><Trash2 size={13} /> Delete</button>
                </div>
              </div>
            ))}
            {locations.data?.length === 0 && <p className="text-[#4A5D6B] text-sm">No locations yet. Add your own space or a partner venue.</p>}
          </div>
        </div>
      )} />}

      {tab === "Bookings" && (
        <div data-testid="enh-bookings-panel">
          <SessionPrep bookings={bookings.data || []} />
          <PosMovedCard what="Your appointments and client payments" testid="enh-pos-moved" />
          <h2 className="font-serif text-2xl text-[#1A242B] mb-1 mt-6">Requests awaiting your response</h2>
          <p className="text-xs text-[#4A5D6B] mb-5">Confirm new requests or propose a better time here. Everything else — scheduling, rescheduling and payments — happens in the POS register.</p>
          <div className="eyw-card divide-y divide-[#E5E0D8]">
            {(bookings.data || []).filter((b) => ["pending", "requested", "reschedule_proposed", "pending_approval"].includes(b.status)).map((b) => (
              <div key={b.id} className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-[#1A242B] flex items-center gap-2"><Calendar size={14} /> {b.service_title}</div>
                  <div className="text-xs text-[#4A5D6B]">{b.name} · {fmtDate(b.date)} · {fmtTimeRange(b.start_time)}{b.status === "reschedule_proposed" && b.proposed_date ? ` → proposed ${b.proposed_date} ${b.proposed_time}` : ""}</div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={b.status} />
                  <AppointmentActions booking={b} onChanged={() => refetch("enh-bookings")} />
                </div>
              </div>
            ))}
            {bookings.data?.length === 0 && <p className="text-[#4A5D6B] text-sm p-4">No bookings yet.</p>}
          </div>
        </div>
      )}

      {tab === "Event Roster" && <EventRoster />}
      {tab === "My Shifts" && <MyShifts />}

      {tab === "Earnings" && (
        <div data-testid="enh-earnings-panel">
          <div className="mb-6"><GiftRedemptionsTable endpoint="/enhancer/me/gift-card-redemptions" title="Gift card redemptions for your sessions" /></div>
          <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
            <h2 className="font-serif text-2xl text-[#1A242B]">Earnings</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="enh-earnings-from" className="eyw-input !py-1.5 text-sm" />
              <span className="text-[#4A5D6B] text-sm">to</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="enh-earnings-to" className="eyw-input !py-1.5 text-sm" />
              {(dateFrom || dateTo) && <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-xs text-[#144F75]">Clear</button>}
              <button onClick={exportEarnings} data-testid="enh-earnings-export" className="eyw-btn-ghost !py-1.5 text-sm"><Download size={15} /> CSV</button>
              <span className="w-px h-6 bg-[#E5E0D8] mx-1 hidden sm:block" />
              <input type="month" value={stmtMonth} onChange={(e) => setStmtMonth(e.target.value)} data-testid="enh-statement-month" className="eyw-input !py-1.5 text-sm" />
              <button onClick={downloadStatement} data-testid="enh-statement-download" className="eyw-btn-ghost !py-1.5 text-sm"><Download size={15} /> Statement (PDF)</button>
            </div>
          </div>
          <section className="eyw-card p-4 sm:p-6 mb-6" data-testid="enh-payouts-section">
            <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Payouts & commissions</h2>
            <p className="text-sm text-[#4A5D6B] mb-4">Your payout account, what you can claim now, and every payout line.</p>
            <div className="grid grid-cols-2 gap-4 max-w-md mb-2">
              <div className="rounded-2xl bg-[#F9F7F3] p-5"><div className="text-3xl font-serif text-[#D98E73]">{money((earnings.data?.pending ?? 0))}</div><p className="text-sm text-[#4A5D6B] mt-1">Pending payout</p></div>
              <div className="rounded-2xl bg-[#F9F7F3] p-5"><div className="text-3xl font-serif text-[#144F75]">{money((earnings.data?.paid ?? 0))}</div><p className="text-sm text-[#4A5D6B] mt-1">Paid out</p></div>
            </div>
            <ConnectPayoutCard />
            <ClaimPanel base="/enhancer" testid="enh-claim" />
            <PartnerLedgerCard />
          </section>
          <MyRentalsCard />

          {earnings.data?.breakdown && (() => {
            const bd = earnings.data.breakdown;
            const cats = [
              { key: "sessions", label: "Sessions & appointments", color: "#144F75" },
              { key: "courses", label: "Courses, classes & packages", color: "#5C8A72" },
              { key: "products", label: "Product sales", color: "#B5794E" },
              { key: "other", label: "Other & adjustments", color: "#8FA89B" },
            ];
            const total = cats.reduce((s, c) => s + Math.max(bd[c.key] || 0, 0), 0) || 1;
            return (
              <div className="eyw-card p-6 mb-6" data-testid="enh-earnings-breakdown">
                <h3 className="font-serif text-lg text-[#1A242B] mb-4">What earns the most</h3>
                <div className="flex h-3 rounded-full overflow-hidden mb-4 bg-[#F0EDE7]">
                  {cats.map((c) => (bd[c.key] > 0 ? <div key={c.key} style={{ width: `${(bd[c.key] / total) * 100}%`, background: c.color }} title={c.label} /> : null))}
                </div>
                <div className="space-y-2">
                  {cats.map((c) => (
                    <div key={c.key} className="flex items-center justify-between text-sm" data-testid={`enh-breakdown-${c.key}`}>
                      <span className="flex items-center gap-2 text-[#4A5D6B]"><span className="w-3 h-3 rounded-full" style={{ background: c.color }} /> {c.label}</span>
                      <span className="font-semibold text-[#1A242B]">${(bd[c.key] || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {earnings.data?.payout_history?.length > 0 && (
            <div className="mb-6" data-testid="enh-payout-history">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif text-lg text-[#1A242B]">Payout history</h3>
                <button onClick={exportPayouts} data-testid="enh-payouts-export" className="eyw-btn-ghost !py-1.5 text-sm"><Download size={15} /> Payouts CSV</button>
              </div>
              <div className="eyw-card divide-y divide-[#E5E0D8]">
                {earnings.data.payout_history.map((b, i) => (
                  <div key={i} className="p-4 flex items-center justify-between" data-testid={`enh-payout-${i}`}>
                    <div className="text-sm text-[#1A242B]">Payout · {b.date || "—"}</div>
                    <div className="text-sm text-[#4A5D6B]">{b.count} {b.count === 1 ? "item" : "items"} · <span className="font-semibold text-[#144F75]">{money(b.amount)}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <h3 className="font-serif text-lg text-[#1A242B] mb-3">Transactions</h3>
          <div className="eyw-card divide-y divide-[#E5E0D8]">
            {(earnings.data?.records || []).map((r) => (
              <div key={r.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-[#1A242B] flex items-center gap-2"><DollarSign size={14} /> {r.service_title || "Offering"}</div>
                  <div className="text-xs text-[#4A5D6B]">Gross {money(r.gross)} · {r.revenue_model} · you get {money(r.enhancer_share)}{r.paid_at ? ` · paid ${r.paid_at.slice(0, 10)}` : ""}</div>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full ${r.payout_status === "paid" ? "bg-[#144F75]/10 text-[#144F75]" : "bg-[#D98E73]/15 text-[#B5794E]"}`}>{r.payout_status}</span>
              </div>
            ))}
            {earnings.data?.records?.length === 0 && <p className="text-[#4A5D6B] text-sm p-4">No earnings in this range — earnings appear after a paid booking.</p>}
          </div>
        </div>
      )}

      {tab === "Orders" && <SellerOrders />}
      {tab === "Analytics" && <CreatorAnalytics />}
      {tab === "Integrations" && <IntegrationsPanel onGoToTab={setTab} />}
      {tab === "Clients" && <MyClients bookings={bookings.data || []} />}
      {tab === "Intake Form" && <IntakeFormEditor endpoint="/enhancer/me/intake-form" />}
      {tab === "Forms" && <FormBuilderPanel />}
      {tab === "Agreements" && <EnhancerAgreements />}


      {svcModal && <ServiceFormModal service={svcModal.id ? svcModal : null} locations={locations.data || []} onClose={() => setSvcModal(null)} onSaved={() => refetch("enh-services")} />}
      {evtModal && <EventFormModal event={evtModal.id ? evtModal : null} onClose={() => setEvtModal(null)} onSaved={() => refetch("enh-events")} />}
      {locModal && <LocationFormModal location={locModal.id ? locModal : null} onClose={() => setLocModal(null)} onSaved={() => refetch("enh-locations")} />}
      {prodModal && <ProductFormModal product={prodModal.id ? prodModal : null} onClose={() => setProdModal(null)} onSaved={() => refetch("enh-products")} />}
      {pkgModal && <PackageFormModal pkg={pkgModal.id ? pkgModal : null} servicesEndpoint="/enhancer/me/services" createEndpoint="/enhancer/packages" updateBase="/enhancer/packages" onClose={() => setPkgModal(null)} onSaved={() => refetch("enh-packages")} />}
      {courseModal && <CourseBuilderModal course={courseModal.id ? courseModal : null} onClose={() => setCourseModal(null)} onSaved={() => refetch("enh-courses")} />}
      {mediaModal && <MediaFormModal item={mediaModal.id ? mediaModal : null} onClose={() => setMediaModal(null)} onSaved={() => refetch("enh-media")} />}
      {stewardModal && <StewardshipFormModal item={stewardModal.id ? stewardModal : null} onClose={() => setStewardModal(null)} onSaved={() => refetch("enh-steward")} />}
    </div>
    </DashboardShell>
  );
}
